import { Extension } from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import {
  exitSuggestion,
  Suggestion,
  type SuggestionKeyDownProps,
  type SuggestionOptions,
  type SuggestionProps,
} from '@tiptap/suggestion';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import {
  findSuggestedTemplateTags,
  TEMPLATE_TAG_SUGGESTION_CHAR,
  type TemplateTagOption,
} from '@ui/components/editor/templateTagUtils';

const TEMPLATE_TAG_SUGGESTION_PRIORITY = 200;
const TEMPLATE_TAG_SUGGESTION_PLUGIN_KEY = new PluginKey('templateTagSuggestion');
const NOT_FOUND_INDEX = -1;

type TemplateTagSuggestionProps = SuggestionProps<TemplateTagOption, TemplateTagOption>;
type TemplateTagSuggestionRenderer = NonNullable<SuggestionOptions<TemplateTagOption, TemplateTagOption>['render']>;

export interface TemplateTagSuggestionState {
  items: TemplateTagOption[];
  highlightedIndex: number;
  clientRect: (() => DOMRect | null) | null;
}

export interface TemplateTagSuggestionBridge {
  subscribe: (listener: () => void) => () => void;
  getState: () => TemplateTagSuggestionState | null;
  highlight: (token: string) => void;
  pick: (token: string) => void;
  dismiss: () => void;
  render: TemplateTagSuggestionRenderer;
}

interface TemplateTagSuggestionOptions {
  bridge: TemplateTagSuggestionBridge | null;
}

interface TemplateTagSuggestionSession {
  props: TemplateTagSuggestionProps | null;
  state: TemplateTagSuggestionState | null;
}

export const createTemplateTagSuggestionBridge = (): TemplateTagSuggestionBridge => {
  const listeners = new Set<() => void>();
  const session: TemplateTagSuggestionSession = { props: null, state: null };

  const publish = (state: TemplateTagSuggestionState | null) => {
    session.state = state;
    listeners.forEach((listener) => listener());
  };

  const open = (props: TemplateTagSuggestionProps) => {
    session.props = props;
    publish({ items: props.items, highlightedIndex: 0, clientRect: props.clientRect ?? null });
  };

  const close = () => {
    session.props = null;
    publish(null);
  };

  const highlightIndex = (index: number) => {
    if (isNil(session.state) || index === NOT_FOUND_INDEX) {
      return;
    }
    publish({ ...session.state, highlightedIndex: index });
  };

  const pickIndex = (index: number) => {
    const item = session.state?.items[index];
    if (isNil(item) || isNil(session.props)) {
      return;
    }
    session.props.command(item);
  };

  const indexOfToken = (token: string): number =>
    session.state?.items.findIndex((item) => item.token === token) ?? NOT_FOUND_INDEX;

  const handleKeyDown = ({ event }: SuggestionKeyDownProps): boolean => {
    const { state } = session;
    if (isNil(state) || isEmpty(state.items)) {
      return false;
    }
    const count = state.items.length;
    switch (event.key) {
      case 'ArrowDown':
        highlightIndex((state.highlightedIndex + 1) % count);
        return true;
      case 'ArrowUp':
        highlightIndex((state.highlightedIndex - 1 + count) % count);
        return true;
      case 'Enter':
      case 'Tab':
        pickIndex(state.highlightedIndex);
        return true;
      case 'Escape':
        event.stopPropagation();
        return true;
      default:
        return false;
    }
  };

  return {
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getState: () => session.state,
    highlight: (token) => highlightIndex(indexOfToken(token)),
    pick: (token) => pickIndex(indexOfToken(token)),
    dismiss: () => {
      const editor = session.props?.editor;
      if (isNil(editor) || editor.isDestroyed) {
        return;
      }
      exitSuggestion(editor.view, TEMPLATE_TAG_SUGGESTION_PLUGIN_KEY);
    },
    render: () => ({ onStart: open, onUpdate: open, onExit: close, onKeyDown: handleKeyDown }),
  };
};

export const TemplateTagSuggestion = Extension.create<TemplateTagSuggestionOptions>({
  name: 'templateTagSuggestion',
  priority: TEMPLATE_TAG_SUGGESTION_PRIORITY,

  addOptions() {
    return { bridge: null };
  },

  addProseMirrorPlugins() {
    const { bridge } = this.options;
    if (isNil(bridge)) {
      return [];
    }
    return [
      Suggestion<TemplateTagOption, TemplateTagOption>({
        editor: this.editor,
        pluginKey: TEMPLATE_TAG_SUGGESTION_PLUGIN_KEY,
        char: TEMPLATE_TAG_SUGGESTION_CHAR,
        allowedPrefixes: null,
        items: ({ editor, query }) => {
          const { tags, isTokenAvailable } = editor.storage.templateTag.getConfiguration();
          return findSuggestedTemplateTags(tags, query, isTokenAvailable);
        },
        command: ({ editor, range, props }) => {
          editor.chain().focus().deleteRange(range).insertTemplateTag(props.token).run();
        },
        render: bridge.render,
      }),
    ];
  },
});
